import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, ChevronDown, ChevronRight, Download } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { value: 'new', label: 'Nouveau', cls: 'bg-blue-500/20 text-blue-300 border-blue-500/50' },
  { value: 'contacte', label: 'Contacté', cls: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/50' },
  { value: 'signe', label: 'Signé', cls: 'bg-green-500/20 text-green-300 border-green-500/50' },
  { value: 'perdu', label: 'Perdu', cls: 'bg-red-500/20 text-red-300 border-red-500/50' },
];

const TYPE_LABEL = { estimation: 'Estimation vente', devis: 'Demande de devis' };

const csvEscape = (val) => {
  if (val === null || val === undefined) return '';
  const s = String(val).replace(/"/g, '""');
  return /[",\n;]/.test(s) ? `"${s}"` : s;
};

const LeadsDashboard = ({ token }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState('all'); // all | estimation | devis | <status>

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [estRes, devRes] = await Promise.all([
        fetch(`${API_URL}/api/estimation/list`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/api/quote-request/list`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      if (!estRes.ok) throw new Error('Erreur chargement estimations');
      if (!devRes.ok) throw new Error('Erreur chargement demandes de devis');
      const estimations = await estRes.json();
      const devis = await devRes.json();
      const merged = [
        ...estimations.map((e) => ({
          ...e,
          _type: 'estimation',
          _label: e.type_bateau || 'Estimation',
        })),
        ...devis.filter((d) => d.service !== 'transmettre-bateau').map((d) => ({
          ...d,
          _type: 'devis',
          _label: d.service_label || 'Devis',
        })),
      ].sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
      setLeads(merged);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) fetchLeads();
  }, [token, fetchLeads]);

  const updateStatus = async (lead, status) => {
    try {
      const endpoint =
        lead._type === 'estimation'
          ? `${API_URL}/api/estimation/${lead.id}/status`
          : `${API_URL}/api/quote-request/${lead.id}/status`;
      const res = await fetch(endpoint, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error('Erreur mise à jour statut');
      setLeads((prev) =>
        prev.map((l) => (l.id === lead.id && l._type === lead._type ? { ...l, status } : l))
      );
    } catch (err) {
      alert(err.message);
    }
  };

  const filteredLeads = leads.filter((l) => {
    if (filter === 'all') return true;
    if (['estimation', 'devis'].includes(filter)) return l._type === filter;
    return (l.status || 'new') === filter;
  });

  const exportCsv = () => {
    const rows = [
      [
        'date',
        'type',
        'sujet',
        'nom',
        'email',
        'telephone',
        'type_bateau',
        'zone',
        'statut',
        'message',
      ],
      ...filteredLeads.map((l) => [
        l.created_at?.slice(0, 19).replace('T', ' ') || '',
        TYPE_LABEL[l._type] || l._type,
        l._label,
        l.nom || '',
        l.email || '',
        l.telephone || '',
        l.type_bateau || '',
        l.zone || '',
        l.status || 'new',
        l.message || '',
      ]),
    ];
    const csv = rows.map((r) => r.map(csvEscape).join(';')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const counts = leads.reduce(
    (acc, l) => {
      acc[l._type] = (acc[l._type] || 0) + 1;
      const st = l.status || 'new';
      acc[`s_${st}`] = (acc[`s_${st}`] || 0) + 1;
      return acc;
    },
    { estimation: 0, devis: 0 }
  );

  return (
    <div
      className="bg-[#302f2c] border border-[#3f4816] rounded-xl overflow-hidden mb-8"
      data-testid="leads-dashboard"
    >
      <div className="p-4 border-b border-[#3f4816] flex flex-wrap items-center gap-3 justify-between">
        <div>
          <h2 className="text-lg font-bold text-[#d9fb06]">
            Leads ({leads.length})
          </h2>
          <p className="text-xs text-[#888680] mt-1">
            {counts.estimation} estimation(s) · {counts.devis} devis · {counts.s_new || 0} nouveau(x)
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="bg-[#1a1c1b] border border-[#3f4816] rounded-lg px-3 py-2 text-sm text-[#dfddd6]"
            data-testid="leads-filter"
          >
            <option value="all">Tous</option>
            <option value="estimation">Estimations</option>
            <option value="devis">Devis</option>
            <option value="new">Statut : Nouveau</option>
            <option value="contacte">Statut : Contacté</option>
            <option value="signe">Statut : Signé</option>
            <option value="perdu">Statut : Perdu</option>
          </select>
          <button
            type="button"
            onClick={exportCsv}
            className="flex items-center gap-2 border border-[#d9fb06] text-[#d9fb06] px-3 py-2 rounded-lg text-sm hover:bg-[#d9fb06] hover:text-[#1a1c1b] transition-colors"
            data-testid="leads-export-csv"
          >
            <Download size={16} />
            Exporter CSV
          </button>
        </div>
      </div>

      {loading ? (
        <div className="p-8 flex items-center justify-center text-[#888680]">
          <Loader2 size={20} className="animate-spin mr-2" /> Chargement…
        </div>
      ) : error ? (
        <div className="p-8 text-center text-red-400">{error}</div>
      ) : filteredLeads.length === 0 ? (
        <div className="p-8 text-center text-[#888680]">
          Aucun lead pour ce filtre.
        </div>
      ) : (
        <div className="divide-y divide-[#3f4816]">
          {filteredLeads.map((lead) => {
            const isOpen = expandedId === `${lead._type}-${lead.id}`;
            const statusMeta =
              STATUS_OPTIONS.find((s) => s.value === (lead.status || 'new')) ||
              STATUS_OPTIONS[0];
            return (
              <div
                key={`${lead._type}-${lead.id}`}
                data-testid={`lead-row-${lead.id}`}
              >
                <div className="p-4 flex items-center gap-3 hover:bg-[#3f4816]/20 transition-colors">
                  <button
                    type="button"
                    onClick={() =>
                      setExpandedId(isOpen ? null : `${lead._type}-${lead.id}`)
                    }
                    className="text-[#888680] hover:text-[#d9fb06]"
                  >
                    {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`px-2 py-0.5 rounded text-xs font-semibold ${
                          lead._type === 'estimation'
                            ? 'bg-cyan-500/20 text-cyan-300'
                            : 'bg-purple-500/20 text-purple-300'
                        }`}
                      >
                        {TYPE_LABEL[lead._type]}
                      </span>
                      <span className="text-[#dfddd6] font-semibold truncate">
                        {lead.nom}
                      </span>
                      <span className="text-[#888680] text-sm">— {lead._label}</span>
                    </div>
                    <p className="text-[#888680] text-xs mt-1">
                      {lead.created_at?.slice(0, 16).replace('T', ' ')} · {lead.email}
                      {lead.telephone ? ` · ${lead.telephone}` : ''}
                    </p>
                  </div>
                  <select
                    value={lead.status || 'new'}
                    onChange={(e) => updateStatus(lead, e.target.value)}
                    className={`px-2 py-1 rounded-lg text-xs font-bold border ${statusMeta.cls} bg-transparent`}
                    data-testid={`lead-status-${lead.id}`}
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value} className="bg-[#1a1c1b]">
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                {isOpen && (
                  <div className="px-4 pb-4 pl-12 text-sm text-[#dfddd6] space-y-1">
                    {lead.zone && <p><span className="text-[#888680]">Zone :</span> {lead.zone}</p>}
                    {lead.type_bateau && <p><span className="text-[#888680]">Type bateau :</span> {lead.type_bateau}</p>}
                    {lead.annee && <p><span className="text-[#888680]">Année :</span> {lead.annee}</p>}
                    {lead.longueur && <p><span className="text-[#888680]">Longueur :</span> {lead.longueur}</p>}
                    {lead.budget && <p><span className="text-[#888680]">Budget :</span> {lead.budget}</p>}
                    {lead.message && (
                      <p className="mt-2 whitespace-pre-wrap"><span className="text-[#888680]">Message :</span> {lead.message}</p>
                    )}
                    <p className="text-xs text-[#888680] mt-2">ID : {lead.id}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LeadsDashboard;
